/**
 * Wire decoding for /api/timing/top-down.
 *
 * The payload's `stocks` used to be an array of objects, which meant spelling
 * out 205 field names for each of 9,780 rows: measured on the real `everything`
 * payload, 77.4% of its 38.99 MB was repeated key names (30.17 MB of keys
 * carrying 8.26 MB of values). It now arrives columnar — the names once, then
 * positional rows — which is 15.81 MB raw and 5.04 MB gzipped (from 7.33).
 *
 * Decoding happens HERE, at the fetch boundary, so every pane downstream keeps
 * receiving the same `StockRow[]` it always did.
 *
 * Both shapes are accepted on purpose. A snapshot is rewritten by the breadth
 * worker, not by the deploy, so immediately after a release the API is still
 * serving rows written by the previous process — and off-RTH that worker is
 * idle, so that window is hours, not seconds.
 */
import type { StockRow } from "@/lib/scanner/topDown";

export type ColumnarStocks = { cols: string[]; rows: unknown[][] };

export function isColumnarStocks(v: unknown): v is ColumnarStocks {
  return (
    !!v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    Array.isArray((v as ColumnarStocks).cols) &&
    Array.isArray((v as ColumnarStocks).rows)
  );
}

export function decodeStocks(v: StockRow[] | ColumnarStocks | undefined | null): StockRow[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (!isColumnarStocks(v)) return [];
  const { cols, rows } = v;
  const n = cols.length;
  const out: StockRow[] = new Array(rows.length);
  for (let r = 0; r < rows.length; r++) {
    const vals = rows[r]!;
    const o: Record<string, unknown> = {};
    for (let c = 0; c < n; c++) o[cols[c]!] = vals[c];
    out[r] = o as unknown as StockRow;
  }
  return out;
}

/**
 * Normalize a fetched payload so `stocks` is always a StockRow[], merging in
 * the separately-fetched fundamentals.
 *
 * Fundamentals are 33.4% of the payload and change daily, while the payload's
 * ETag moves every ~72s, so they are served as their own resource
 * (/api/timing/top-down/fundamentals) and stitched back on by symbol here —
 * one place, so nothing downstream can observe a row without them.
 *
 * A row keeps `fundamentals: null` until that fetch lands. It is the same
 * shape a symbol with no fundamentals data has, which every consumer already
 * handles (`r.fundamentals?.pe_ratio`), so the gap renders as an empty cell
 * rather than a crash.
 */
export function decodeTopDownPayload<T extends { stocks?: unknown }>(
  p: T | null,
  fundamentals?: Record<string, unknown> | null,
): T | null {
  if (!p) return p;
  const stocks = Array.isArray(p.stocks)
    ? (p.stocks as StockRow[])
    : isColumnarStocks(p.stocks)
      ? decodeStocks(p.stocks)
      : null;
  if (!stocks) return p;
  if (fundamentals) {
    for (const s of stocks) {
      const row = s as unknown as Record<string, unknown>;
      // FILL, never overwrite. A snapshot is rewritten by the worker, not by
      // the deploy, so straight after a release the grid payload still carries
      // fundamentals inline while this resource is still empty — and off-RTH
      // the worker is idle, so that window is hours. Clobbering would blank
      // every fundamentals cell in the app for the whole evening.
      if (row.fundamentals !== undefined) continue;
      // Uppercase, because that is how every top-down table is keyed. 17
      // preferred-share tickers carry a lowercase class letter (CDRpB, CMSpB,
      // FITBpA, …), and matching on the raw symbol silently handed those rows
      // a null instead of their fundamentals — a blank cell, not an error.
      row.fundamentals = fundamentals[(s as { symbol: string }).symbol.toUpperCase()] ?? null;
    }
  }
  return { ...p, stocks };
}
